import AppLayout from '@/Layouts/AppLayout';
import PageHeader from '@/Components/PageHeader';
import StatsCard from '@/Components/StatsCard';
import Avatar from '@/Components/Avatar';
import { Link } from '@inertiajs/react';

export default function Dashboard() {
    const stats = [
        { icon: 'bi-people', label: 'Total Contacts', value: '2,847', change: '+12.5%', changeType: 'positive', variant: 'primary' },
        { icon: 'bi-currency-dollar', label: 'Revenue', value: '$48,294', change: '+8.2%', changeType: 'positive', variant: 'success' },
        { icon: 'bi-airplane', label: 'Active Bookings', value: '156', change: '+24.3%', changeType: 'positive', variant: 'info' },
        { icon: 'bi-check2-circle', label: 'Completed Deals', value: '89', change: '-3.1%', changeType: 'negative', variant: 'warning' },
    ];

    const recentBookings = [
        { id: 1, customer: 'John Doe', destination: 'Maldives', date: '2024-02-15', status: 'Confirmed', amount: '$4,500' },
        { id: 2, customer: 'Jane Smith', destination: 'Paris', date: '2024-02-18', status: 'Pending', amount: '$2,800' },
        { id: 3, customer: 'Mike Johnson', destination: 'Tokyo', date: '2024-02-20', status: 'Confirmed', amount: '$5,200' },
        { id: 4, customer: 'Sarah Williams', destination: 'Dubai', date: '2024-02-22', status: 'Processing', amount: '$3,900' },
        { id: 5, customer: 'Chris Brown', destination: 'Bali', date: '2024-02-25', status: 'Confirmed', amount: '$2,100' },
    ];

    const upcomingTasks = [
        { id: 1, title: 'Follow up with client about Maldives trip', priority: 'High', due: 'Today' },
        { id: 2, title: 'Send quotation to Johnson family', priority: 'Medium', due: 'Tomorrow' },
        { id: 3, title: 'Confirm hotel booking for Dubai trip', priority: 'High', due: 'Today' },
        { id: 4, title: 'Review supplier contract renewal', priority: 'Low', due: 'Next Week' },
    ];

    const getStatusBadge = (status) => {
        const variants = {
            'Confirmed': 'badge-success',
            'Pending': 'badge-warning',
            'Processing': 'badge-info',
            'Cancelled': 'badge-danger',
        };
        return variants[status] || 'badge-secondary';
    };

    const getPriorityBadge = (priority) => {
        const variants = {
            'High': 'badge-danger',
            'Medium': 'badge-warning',
            'Low': 'badge-secondary',
        };
        return variants[priority] || 'badge-secondary';
    };

    return (
        <AppLayout title="Dashboard">
            <PageHeader
                title="Dashboard"
                subtitle="Welcome back! Here's what's happening with your business today."
                actions={
                    <>
                        <button className="btn btn-light">
                            <i className="bi bi-download"></i>
                            <span>Export</span>
                        </button>
                        <button className="btn btn-primary">
                            <i className="bi bi-plus-lg"></i>
                            <span>New Booking</span>
                        </button>
                    </>
                }
            />

            {/* Stats Cards */}
            <div className="row g-3 mb-4">
                {stats.map((stat, idx) => (
                    <div key={idx} className="col-12 col-sm-6 col-xl-3">
                        <StatsCard {...stat} />
                    </div>
                ))}
            </div>

            <div className="row g-3">
                {/* Recent Bookings */}
                <div className="col-12 col-xl-8">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title">Recent Bookings</h5>
                            <Link href="/bookings" className="btn btn-sm btn-light">
                                View All
                            </Link>
                        </div>
                        <div className="table-responsive">
                            <table className="table mb-0">
                                <thead>
                                    <tr>
                                        <th>Customer</th>
                                        <th>Destination</th>
                                        <th>Date</th>
                                        <th>Status</th>
                                        <th className="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentBookings.map((booking) => (
                                        <tr key={booking.id}>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <Avatar name={booking.customer} size="sm" />
                                                    <span className="fw-medium">{booking.customer}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <i className="bi bi-geo-alt text-muted me-1"></i>
                                                {booking.destination}
                                            </td>
                                            <td className="text-muted">{booking.date}</td>
                                            <td>
                                                <span className={`badge ${getStatusBadge(booking.status)}`}>
                                                    {booking.status}
                                                </span>
                                            </td>
                                            <td className="text-end fw-semibold">{booking.amount}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Upcoming Tasks */}
                <div className="col-12 col-xl-4">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title">Upcoming Tasks</h5>
                            <Link href="/tasks" className="btn btn-sm btn-light">
                                View All
                            </Link>
                        </div>
                        <div className="card-body p-0">
                            <div className="list-group list-group-flush">
                                {upcomingTasks.map((task) => (
                                    <div key={task.id} className="list-group-item px-3 py-3">
                                        <div className="d-flex align-items-start gap-3">
                                            <div className="form-check mt-1">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id={`task-${task.id}`}
                                                />
                                            </div>
                                            <div className="flex-grow-1">
                                                <div className="fw-medium mb-1">{task.title}</div>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className={`badge ${getPriorityBadge(task.priority)}`}>
                                                        {task.priority}
                                                    </span>
                                                    <small className="text-muted">
                                                        <i className="bi bi-clock me-1"></i>
                                                        {task.due}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="card-footer text-center">
                            <Link href="/tasks/create" className="btn btn-sm btn-primary">
                                <i className="bi bi-plus-lg me-1"></i>
                                Add Task
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="row g-3 mt-2">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title">Quick Actions</h5>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                {[
                                    { icon: 'bi-person-plus', label: 'Add Contact', href: '/contacts/create', color: 'primary' },
                                    { icon: 'bi-building-add', label: 'Add Company', href: '/companies/create', color: 'success' },
                                    { icon: 'bi-airplane-engines', label: 'New Booking', href: '/bookings/create', color: 'info' },
                                    { icon: 'bi-file-text', label: 'Create Quote', href: '/quotations/create', color: 'warning' },
                                    { icon: 'bi-calendar-plus', label: 'Schedule Meeting', href: '/meetings/create', color: 'danger' },
                                    { icon: 'bi-box-seam', label: 'Create Package', href: '/packages/create', color: 'secondary' },
                                ].map((action, idx) => (
                                    <div key={idx} className="col-6 col-md-4 col-lg-2">
                                        <Link
                                            href={action.href}
                                            className="d-flex flex-column align-items-center p-3 rounded-3 text-decoration-none hover-lift"
                                            style={{ background: '#f8fafc', transition: 'all 0.2s ease' }}
                                        >
                                            <div
                                                className="d-flex align-items-center justify-content-center rounded-3 mb-2"
                                                style={{
                                                    width: 48,
                                                    height: 48,
                                                    background: `var(--bs-${action.color})`,
                                                    color: 'white',
                                                    fontSize: '1.25rem',
                                                }}
                                            >
                                                <i className={`bi ${action.icon}`}></i>
                                            </div>
                                            <span className="text-center small fw-medium text-dark">
                                                {action.label}
                                            </span>
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
}
